import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { generatePDFReport } from '../utils/pdfGenerator';
import { calculateRiskScore } from '../utils/riskCalculator';

const Results = ({ assessmentData }) => {
  const navigate = useNavigate();
  const [riskScore, setRiskScore] = useState(assessmentData.riskScore || 0);
  const [riskLevel, setRiskLevel] = useState(assessmentData.riskLevel || 'Low Risk');

  useEffect(() => {
    if (!assessmentData.riskScore && assessmentData.responses) {
      try {
        const { score, level } = calculateRiskScore(assessmentData.responses, assessmentData.sectionA);
        setRiskScore(score);
        setRiskLevel(level);
      } catch (error) {
        console.error('Error calculating risk:', error);
      }
    }
  }, [assessmentData]);

  const getRiskClass = (level) => {
    const levelMap = {
      'Low Risk': 'risk-low',
      'Moderate Risk': 'risk-moderate',
      'High Risk': 'risk-high',
      'Critical Risk': 'risk-critical'
    };
    return levelMap[level] || 'risk-low';
  };

  const getRecommendations = (level, score) => {
    const recommendations = {
      'Low Risk': [
        'Maintain current compliance practices',
        'Continue regular audits and reviews',
        'Keep documentation up to date',
        'Monitor for regulatory updates'
      ],
      'Moderate Risk': [
        'Establish a Data Protection Officer (DPO) role',
        'Implement comprehensive data governance framework',
        'Create and maintain all required policies (Privacy Policy, Data Retention Policy, etc.)',
        'Establish clear consent management processes',
        'Set up data principal rights workflow',
        'Conduct regular security assessments',
        'Implement vendor risk management program'
      ],
      'High Risk': [
        'Immediately appoint a Data Protection Officer (DPO)',
        'Develop and implement comprehensive data governance framework',
        'Create all required documentation (Privacy Policy, Data Retention Policy, Consent Management Policy, Breach Management SOP, Vendor Risk Policy, Data Accuracy & Correction SOP)',
        'Establish secure data deletion and anonymization processes',
        'Implement robust access controls and encryption',
        'Conduct vulnerability assessments and penetration testing',
        'Establish breach notification and incident response procedures',
        'Review and secure all third-party vendor relationships with proper Data Sharing Agreements (DSA)',
        'Implement employee training programs on DPDP compliance',
        'Ensure top management awareness of DPDP obligations'
      ],
      'Critical Risk': [
        'URGENT: Appoint a Data Protection Officer (DPO) immediately',
        'URGENT: Conduct comprehensive data inventory and mapping',
        'URGENT: Implement all required policies and procedures',
        'URGENT: Secure all sensitive data (financial, health, biometrics)',
        'URGENT: Establish breach management and incident response procedures',
        'URGENT: Review and secure all cross-border data transfers',
        'URGENT: Implement comprehensive security controls (encryption, access controls, MFA)',
        'URGENT: Conduct security audits and vulnerability assessments',
        'URGENT: Establish vendor risk management with proper DSAs',
        'URGENT: Implement employee training and awareness programs',
        'URGENT: Ensure Board-level/CXO-level oversight for privacy',
        'Consider engaging external compliance consultants',
        'Prioritize remediation of highest-risk areas first'
      ]
    };

    return recommendations[level] || recommendations['Low Risk'];
  };

  const handleGeneratePDF = () => {
    const recommendations = getRecommendations(riskLevel, riskScore);
    generatePDFReport(assessmentData, riskScore, riskLevel, recommendations);
  };

  const handlePrint = () => {
    window.print();
  };

  const today = new Date().toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });

  const contactBlockStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px 32px',
    marginBottom: 'clamp(28px, 3vw, 40px)',
    padding: 'clamp(20px, 2.5vw, 28px) 0',
    borderTop: '1px solid rgba(26, 10, 34, 0.15)',
    borderBottom: '1px solid rgba(26, 10, 34, 0.15)'
  };
  const contactLabelStyle = {
    fontFamily: 'var(--mono)',
    fontSize: '10px',
    letterSpacing: '0.24em',
    textTransform: 'uppercase',
    color: 'var(--muted)',
    marginBottom: '6px'
  };
  const contactValueStyle = {
    fontFamily: 'var(--sans)',
    fontSize: '15px',
    color: 'var(--ink)',
    lineHeight: 1.4,
    wordBreak: 'break-word'
  };

  const company = assessmentData.companyName || assessmentData.organizationName;
  const phone =
    assessmentData.phone ||
    (assessmentData.phoneCountryCode && assessmentData.phoneNumber
      ? `${assessmentData.phoneCountryCode}${assessmentData.phoneNumber}`
      : null);

  return (
    <div className="results-container">
      <div className="section-title-eyebrow">
        <span>Assessment</span>
        <span className="dot">◆</span>
        <span>Results</span>
        <span className="dot">◆</span>
        <span>{today}</span>
      </div>
      <h2 className="section-title">
        Assessment <em>results.</em>
      </h2>
      <p className="section-description">
        Your DPDP Act 2023 readiness snapshot, with a quantified risk score and the next moves
        we'd prioritise for {company || 'your organisation'}.
      </p>

      {(company || assessmentData.name || assessmentData.email || phone) && (
        <div style={contactBlockStyle}>
          {company && (
            <div>
              <div style={contactLabelStyle}>Company</div>
              <div style={contactValueStyle}>{company}</div>
            </div>
          )}
          {assessmentData.name && (
            <div>
              <div style={contactLabelStyle}>Contact</div>
              <div style={contactValueStyle}>{assessmentData.name}</div>
            </div>
          )}
          {assessmentData.email && (
            <div>
              <div style={contactLabelStyle}>Email</div>
              <div style={contactValueStyle}>{assessmentData.email}</div>
            </div>
          )}
          {phone && (
            <div>
              <div style={contactLabelStyle}>Phone</div>
              <div style={contactValueStyle}>{phone}</div>
            </div>
          )}
        </div>
      )}

      <div className="stat-row">
        <div>
          <div className="stat-num">{riskScore}</div>
          <div className="stat-label">Risk Score</div>
        </div>
        <div>
          <div className={`risk-badge ${getRiskClass(riskLevel)}`}>{riskLevel}</div>
          <div className="stat-label" style={{ marginTop: '14px' }}>Risk Level</div>
        </div>
      </div>

      <div className="risk-explanation" style={{ marginBottom: 'clamp(36px, 4vw, 56px)' }}>
        <div className="section-title-eyebrow" style={{ marginBottom: '12px' }}>
          <span>Reference</span>
          <span className="dot">◆</span>
          <span>Risk Bands</span>
        </div>
        <h3
          style={{
            fontFamily: 'var(--serif)',
            fontWeight: 400,
            fontSize: 'clamp(22px, 2.4vw, 30px)',
            letterSpacing: '-0.02em',
            margin: 0,
            color: 'var(--ink)'
          }}
        >
          Risk <em style={{ fontStyle: 'italic', color: 'var(--accent)' }}>classification.</em>
        </h3>

        <div className="numbered-grid">
          <div className="num-item">
            <div className="num-item-num">01 ◆ Low</div>
            <h4>0 – 20</h4>
            <p>Basic data, low volume, minimal SaaS reliance, strong controls.</p>
          </div>
          <div className="num-item">
            <div className="num-item-num">02 ◆ Moderate</div>
            <h4>21 – 40</h4>
            <p>Customer-facing, collects IDs or financial data, moderate SaaS footprint, partial governance.</p>
          </div>
          <div className="num-item">
            <div className="num-item-num">03 ◆ High</div>
            <h4>41 – 60</h4>
            <p>Sensitive data, large volume, weak vendor controls, no policies in place.</p>
          </div>
          <div className="num-item">
            <div className="num-item-num">04 ◆ Critical</div>
            <h4>&gt; 60</h4>
            <p>Financial/healthcare, minors' data, cross-border transfers, weak controls, no DPO.</p>
          </div>
        </div>
      </div>

      <div className="recommendations">
        <div className="section-title-eyebrow" style={{ marginBottom: '12px' }}>
          <span>Next Steps</span>
          <span className="dot">◆</span>
          <span>Action Plan</span>
        </div>
        <h3>
          Recommended <em style={{ fontStyle: 'italic', color: 'var(--accent)' }}>actions.</em>
        </h3>
        <ul>
          {getRecommendations(riskLevel, riskScore).map((rec, index) => (
            <li key={index}>{rec}</li>
          ))}
        </ul>
      </div>

      <div className="button-group">
        <button className="btn btn-secondary" onClick={() => navigate('/section-b')}>
          ← Review answers
        </button>
        <button className="btn btn-ghost" onClick={handlePrint}>
          Print Report
        </button>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          Start New Assessment
        </button>
        <button className="btn btn-primary" onClick={handleGeneratePDF}>
          Download PDF Report <span className="arrow">→</span>
        </button>
      </div>
    </div>
  );
};

export default Results;
